// Demo auth — hardcoded credentials, localStorage session.
//
// PRODUCTION PATH (per the engagement brief): replace this entire module
// with MSAL.js + Entra ID SSO. The vendor brief requires Azure AD as the
// only acceptable identity provider. The hook signature stays the same so
// the call sites in App.tsx don't need to change.
//
// Why hardcoded for the demo: this is a static-only deployment with no
// backend, no token verification surface, and no real protected resources
// (the synthetic data is the same regardless). The login screen is
// theatre — it gates casual URL-walkers and makes the demo feel real.

import { useCallback, useEffect, useState } from "react";

// Login gate toggle.
// While `false`, the app skips the login screen entirely and lands straight
// on the dashboard with a default demo identity — for client walkthroughs
// where credentials only get in the way. Flip to `true` to re-enable the
// hardcoded demo-account gate below (nothing else needs to change). The
// production path still replaces this whole module with MSAL.js + Entra ID.
export const AUTH_ENABLED = true;

const STORAGE_KEY = "cs.auth";
const SESSION_HOURS = 12;

// Identity shown while the login gate is disabled.
const GUEST_USER: AuthUser = {
  email: "demo@cashflowsentinel.com",
  name: "Cashflow Sentinel",
  role: "CFO · enterprise",
  signedInAt: 0,
};

// Allowed accounts.
const ACCOUNTS: Record<string, { password: string; name: string; role: string }> = {
  "demo@cashflowsentinel.com": {
    password: "sentinel2026",
    name: "Cashflow Sentinel",
    role: "CFO · enterprise",
  },
  "viewer@cashflowsentinel.com": {
    password: "viewer",
    name: "Cashflow Sentinel",
    role: "Viewer · read-only",
  },
};

export interface AuthUser {
  email: string;
  name: string;
  role: string;
  signedInAt: number;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
}

function loadFromStorage(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthUser;
    const ageHours = (Date.now() - parsed.signedInAt) / 3_600_000;
    if (ageHours > SESSION_HOURS) return null;
    // Self-heal: if ACCOUNTS metadata changed since this session was created
    // (renamed display name, role, etc.), prefer the live values.
    const account = ACCOUNTS[parsed.email];
    if (account) {
      return { ...parsed, name: account.name, role: account.role };
    }
    return parsed;
  } catch {
    return null;
  }
}

export function useAuth() {
  const [state, setState] = useState<AuthState>(
    AUTH_ENABLED ? { user: null, loading: true } : { user: GUEST_USER, loading: false },
  );

  useEffect(() => {
    if (!AUTH_ENABLED) return; // login disabled — guest identity already set
    setState({ user: loadFromStorage(), loading: false });
  }, []);

  const signIn = useCallback((email: string, password: string): { ok: true } | { ok: false; error: string } => {
    const key = email.trim().toLowerCase();
    const account = ACCOUNTS[key];
    if (!account) return { ok: false, error: "No account with that email." };
    if (account.password !== password) return { ok: false, error: "Incorrect password." };
    const user: AuthUser = {
      email: key,
      name: account.name,
      role: account.role,
      signedInAt: Date.now(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } catch {}
    setState({ user, loading: false });
    return { ok: true };
  }, []);

  const signOut = useCallback(() => {
    if (!AUTH_ENABLED) return; // no login gate to return to while disabled
    try {
      localStorage.removeItem(STORAGE_KEY);
      // The Chat API key is scoped to the login session — drop it on sign-out
      // so a key never outlives the session that entered it.
      sessionStorage.removeItem("cs.chat.llm");
    } catch {}
    setState({ user: null, loading: false });
  }, []);

  return { ...state, signIn, signOut };
}

// Credentials are not exposed from the auth module. The login screen does
// not reveal them either — they must be transmitted to authorised demo
// users out-of-band (e.g., a secure note in the meeting invite).
//
// Production replaces this hardcoded-account list with Entra ID via
// MSAL.js — at which point there's no credential surface in the SPA at all.
